import React from 'react';
import { Drawer } from '../ui/Drawer';
import type { CustomDrawerProps } from './TablemaintPage';
import { ack997BadgeClass, ack997MatchNote } from '../../utils/edi997';

/**
 * Detail view for one EDI 997 acknowledgment line (cms.edi_997).
 *
 * DISPLAY ONLY — there is no save path, no delete, and no editable input anywhere in
 * here. cms.edi_997 is written solely by edi_kleinschmidt_inbound.pl (process997) and
 * exists for reporting, so the whole surface is read-only: the backend engine has
 * readonly=True, every registry field is editable:false, and this drawer offers a
 * single Close button. It is here because the grid's diagnostic tail — the ack file
 * path, Kleinschmidt's control number, the source-record pointer — is what a support
 * question actually needs, and none of it reads well in a table cell.
 *
 * Reads the row handed over by the grid rather than refetching: the list query already
 * returns every column plus the `ackresult` label, and a GET-single would come back
 * with raw zero-dates instead of the grid's formatted ones.
 */
const s = (v: any): string => (v == null ? '' : String(v));

const Field: React.FC<{ label: string; wide?: boolean; children?: React.ReactNode; note?: React.ReactNode }> = ({
  label,
  wide,
  children,
  note,
}) => (
  <div className={`flex flex-col${wide ? ' md:col-span-2' : ''}`}>
    <span className="text-xs font-medium text-muted mb-1">{label}</span>
    <div className="px-3 py-2 min-h-[38px] text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg flex items-center justify-between gap-2">
      <span className="break-all">
        {children == null || children === '' ? <span className="text-gray-400">—</span> : children}
      </span>
      {note}
    </div>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
    <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted">{title}</legend>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
  </fieldset>
);

const Edi997DetailDrawer: React.FC<CustomDrawerProps> = ({ row, onClose }) => {
  const r = row ?? {};
  const code = s(r.acceptreject);
  const matchNote = ack997MatchNote(s(r.matchstatus));

  const footer = (
    <div className="flex items-center justify-end">
      <button
        onClick={onClose}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
      >
        Close
      </button>
    </div>
  );

  return (
    <Drawer
      isOpen
      onClose={onClose}
      title={`997 Acknowledgment — ${s(r.doctype) || 'EDI'} ${s(r.refnumber)}`.trim()}
      position="right"
      width="46rem"
      footer={footer}
    >
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <span className={`px-2.5 py-1 rounded text-sm font-semibold whitespace-nowrap ${ack997BadgeClass(code)}`}>
            {s(r.ackresult) || code || 'No code'}
          </span>
          <span className="text-xs text-muted">
            Read-only — acknowledgments are recorded by the inbound EDI process.
          </span>
        </div>

        <Section title="Acknowledgment">
          <Field label="Ack Date/Time">{s(r.ackdatetime)}</Field>
          <Field label="Process Date">{s(r.processdate)}</Field>
          <Field label="Document Type">{s(r.doctype)}</Field>
          <Field label="Reference #">{s(r.refnumber)}</Field>
          <Field label="Code">{code}</Field>
          <Field label="Result">{s(r.ackresult)}</Field>
        </Section>

        <Section title="Partner">
          <Field label="EDI Handle">{s(r.edihandle)}</Field>
          {/* The ID the ACK arrived under, which is not always the ID we send to —
              MercuryGate returns every ack as MGOHL (kleinschmidt_partners.ack_isagsid). */}
          <Field label="ISA/GS ID">{s(r.isagsid)}</Field>
          {/* Kleinschmidt re-envelopes our flat files with their own ISA/GS/ST counters,
              so this is their control number, kept for support and never used to match. */}
          <Field label="Control # (Kleinschmidt)" wide>
            {s(r.controlnumber)}
          </Field>
        </Section>

        <Section title="Matched Record">
          <Field label="Pro Number">{s(r.pronumber)}</Field>
          <Field
            label="Match Status"
            note={
              matchNote ? (
                <span title={matchNote} className="text-xs text-muted whitespace-nowrap cursor-help">
                  ⓘ
                </span>
              ) : null
            }
          >
            {s(r.matchstatus)}
          </Field>
          <Field label="Source Table">{s(r.tablename)}</Field>
          <Field label="Source Recno">{s(r.tablerecno)}</Field>
          {/* 214 only: the matched event's own name, copied onto the ack row at match
              time because history_edi_events.pl later moves that row to
              edi_events_archive and the pointer alone stops resolving. */}
          <Field label="Event" wide>
            {s(r.event)}
          </Field>
          {matchNote && <p className="md:col-span-2 text-xs text-muted">{matchNote}</p>}
        </Section>

        <Section title="Ack File">
          <Field label="File" wide>
            {s(r.edifilename)}
          </Field>
          <Field label="Line #">{s(r.linenumber)}</Field>
          <Field label="Processed">{s(r.createdatetime)}</Field>
          <Field label="Recno">{s(r.recno)}</Field>
          <Field label="Deleted">{s(r.deleted)}</Field>
        </Section>
      </div>
    </Drawer>
  );
};

export default Edi997DetailDrawer;
